// src/components/FacturasProfesionales.jsx

import { useMemo, useRef, useState, useEffect } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import logo from "../assets/LOGO - FOOTER.png";
import "./FacturaView.css";

// ======================================================
// DATOS DEL CENTRO
// ======================================================

const CENTRO = {
  nombre: "TE AYUDO",
  direccion: "Avenida Alcalde Jesús Mantaras, local 5",
  localidad: "Jerez de la Frontera, Cádiz 11405",
  telefono: "+34 601 866 199",
};

// ======================================================
// EMISORES DISPONIBLES
// ======================================================
//
// Dejamos únicamente a Teresa y Américo.
// Teresa tiene sus datos precargados.
// En Américo el NIF queda editable por si se necesita
// utilizarlo más adelante.
// ======================================================

const EMISORES = [
  {
    id: "teresa",
    nombre: "Teresa Hernandez Cardenas",
    nif: "77172767Q",
    rol: "Administrador",
    cuenta: "ES80 2100 2545 1902 1042 0805",
  },
  {
    id: "americo",
    nombre: "Américo",
    nif: "",
    rol: "Administrador",
    cuenta: "",
  },
];

// ======================================================
// HELPERS NUMERACIÓN
// ======================================================

const padNumber = (n, size = 4) =>
  String(n).padStart(size, "0");

const getYearFromMes = (fechaMes) =>
  fechaMes
    ? String(fechaMes).slice(0, 4)
    : String(new Date().getFullYear());

const getCounterKey = (tipo, year) =>
  `teayudo_facturas_${tipo}_${year}`;

const getNextCounter = (tipo, year) => {
  const saved = Number(
    localStorage.getItem(getCounterKey(tipo, year)) || 0
  );

  return saved + 1;
};

const saveCounter = (tipo, year, numero) => {
  localStorage.setItem(
    getCounterKey(tipo, year),
    String(numero)
  );
};

const buildNumeroDocumentoManual = (
  fechaMes,
  tipo = "Factura",
  numero
) => {
  const year = getYearFromMes(fechaMes);

  const prefijo =
    String(tipo || "").toLowerCase() === "presupuesto"
      ? "PRE"
      : "FAC";

  return `${prefijo}-${year}-${padNumber(numero)}`;
};

// ======================================================
// COMPONENTE
// ======================================================

export default function FacturasProfesionales({
  mes,
  setMes,
  loading,
  error,
}) {
  const facturaRef = useRef();

  const year = getYearFromMes(mes);

  // ------------------------------------------------------
  // CONTADORES
  // ------------------------------------------------------

  const [numeroFactura, setNumeroFactura] = useState(() =>
    getNextCounter("factura", getYearFromMes(mes))
  );

  const [numeroPresupuesto, setNumeroPresupuesto] = useState(
    () =>
      getNextCounter(
        "presupuesto",
        getYearFromMes(mes)
      )
  );

  useEffect(() => {
    setNumeroFactura(
      getNextCounter("factura", year)
    );

    setNumeroPresupuesto(
      getNextCounter("presupuesto", year)
    );
  }, [year]);

  // ------------------------------------------------------
  // DOCUMENTO
  // ------------------------------------------------------

  const [aplicaIva, setAplicaIva] = useState(true);

  const [documento, setDocumento] = useState({
    tipo: "Factura",
    fecha: new Date().toISOString().slice(0, 10),

    emisor_id: "teresa",
    emisor_nombre: EMISORES[0].nombre,
    emisor_nif: EMISORES[0].nif,
    emisor_rol: EMISORES[0].rol,

    nombre: "",
    nif: "",

    cuenta: EMISORES[0].cuenta,

    condicionesPago: "",
  });

  // ------------------------------------------------------
  // SERVICIOS
  // ------------------------------------------------------
  //
  // Cada factura puede tener tantas líneas como se quiera.
  // ------------------------------------------------------

  const [servicios, setServicios] = useState([
    {
      id: 1,
      concepto: "",
      importe: "",
    },
  ]);

  const crearIdServicio = () =>
    Date.now() + Math.random();

  const añadirServicio = () => {
    setServicios((prev) => [
      ...prev,
      {
        id: crearIdServicio(),
        concepto: "",
        importe: "",
      },
    ]);
  };

  const actualizarServicio = (
    id,
    campo,
    valor
  ) => {
    setServicios((prev) =>
      prev.map((servicio) =>
        servicio.id === id
          ? {
              ...servicio,
              [campo]: valor,
            }
          : servicio
      )
    );
  };

  const eliminarServicio = (id) => {
    setServicios((prev) => {
      if (prev.length === 1) {
        return [
          {
            ...prev[0],
            concepto: "",
            importe: "",
          },
        ];
      }

      return prev.filter(
        (servicio) => servicio.id !== id
      );
    });
  };

  // ------------------------------------------------------
  // EMISOR
  // ------------------------------------------------------

  const seleccionarEmisor = (emisorId) => {
    const emisor = EMISORES.find(
      (item) => item.id === emisorId
    );

    if (!emisor) return;

    setDocumento((prev) => ({
      ...prev,

      emisor_id: emisor.id,
      emisor_nombre: emisor.nombre,
      emisor_nif: emisor.nif,
      emisor_rol: emisor.rol,

      // Teresa tiene la cuenta precargada.
      // Américo queda vacío para poder rellenarla
      // manualmente si algún día se utiliza.
      cuenta: emisor.cuenta || "",
    }));
  };

  // ------------------------------------------------------
  // CÁLCULOS
  // ------------------------------------------------------

  const base = useMemo(() => {
    return servicios.reduce(
      (total, servicio) =>
        total +
        (Number(servicio.importe) || 0),
      0
    );
  }, [servicios]);

  const iva = aplicaIva
    ? base * 0.21
    : 0;

  const total = base + iva;

  const numeroActual =
    documento.tipo === "Presupuesto"
      ? numeroPresupuesto
      : numeroFactura;

  const numeroDocumento =
    buildNumeroDocumentoManual(
      mes,
      documento.tipo,
      numeroActual
    );

  const serviciosValidos = useMemo(
    () =>
      servicios.filter(
        (servicio) =>
          String(
            servicio.concepto || ""
          ).trim() &&
          Number(servicio.importe) > 0
      ),
    [servicios]
  );

  const documentoValido =
    base > 0 &&
    serviciosValidos.length > 0;

  // ------------------------------------------------------
  // PDF
  // ------------------------------------------------------

  const handleDownloadPDF = async () => {
    const input = facturaRef.current;

    if (!input || !documentoValido) {
      return;
    }

    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
    });

    const imgData =
      canvas.toDataURL("image/png");

    const pdf = new jsPDF(
      "p",
      "mm",
      "a4"
    );

    const pageWidth =
      pdf.internal.pageSize.getWidth();

    const pageHeight =
      pdf.internal.pageSize.getHeight();

    const imgWidth = pageWidth;

    const imgHeight =
      (canvas.height * imgWidth) /
      canvas.width;

    // Si el documento cabe en una página
    if (imgHeight <= pageHeight) {
      pdf.addImage(
        imgData,
        "PNG",
        0,
        0,
        imgWidth,
        imgHeight
      );
    } else {
      // Si hay muchos servicios, permitimos
      // que el PDF ocupe varias páginas.
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(
        imgData,
        "PNG",
        0,
        position,
        imgWidth,
        imgHeight
      );

      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position =
          heightLeft - imgHeight;

        pdf.addPage();

        pdf.addImage(
          imgData,
          "PNG",
          0,
          position,
          imgWidth,
          imgHeight
        );

        heightLeft -= pageHeight;
      }
    }

    const tipoArchivo =
      documento.tipo
        .toLowerCase()
        .replace(/\s+/g, "_");

    pdf.save(
      `${tipoArchivo}_${numeroDocumento}_${
        mes || "sin_mes"
      }.pdf`
    );

    // Guardamos el contador solamente
    // después de generar el documento.
    if (
      documento.tipo === "Presupuesto"
    ) {
      saveCounter(
        "presupuesto",
        year,
        numeroPresupuesto
      );

      setNumeroPresupuesto(
        (prev) => prev + 1
      );
    } else {
      saveCounter(
        "factura",
        year,
        numeroFactura
      );

      setNumeroFactura(
        (prev) => prev + 1
      );
    }
  };

  // ------------------------------------------------------
  // MES EN TEXTO
  // ------------------------------------------------------

  const mesTexto = useMemo(() => {
    if (!mes) return "";

    const d = new Date(`${mes}-01T12:00:00`);

    if (Number.isNaN(d.getTime())) {
      return mes;
    }

    return d.toLocaleDateString(
      "es-ES",
      {
        month: "long",
        year: "numeric",
      }
    );
  }, [mes]);

  // ======================================================
  // RENDER
  // ======================================================

  return (
    <div className="cierre-panel facturas-profesionales">
      {/* MES */}

      <div className="pagos-actions">
        <div className="month-filter">
          <label>
            Mes:
            <input
              type="month"
              value={mes}
              onChange={(e) =>
                setMes(e.target.value)
              }
            />
          </label>
        </div>
      </div>

      {loading ? (
        <p>Cargando datos...</p>
      ) : error ? (
        <p style={{ color: "red" }}>
          {error}
        </p>
      ) : (
        <>
          <h3 className="facturas-title">
            Facturas y presupuestos{" "}
            {mesTexto || mes}
          </h3>

          <section className="factura-card">
            {/* CABECERA */}

            <div className="factura-card-header">
              <h4>
                Factura / Presupuesto personalizado
              </h4>

              <span className="tag tag-dinamica">
                {documento.tipo}
              </span>
            </div>

            {/* ========================================= */}
            {/* DATOS DEL DOCUMENTO */}
            {/* ========================================= */}

            <div className="factura-profesional-form factura-profesional-form--grid">
              <label>
                Tipo:
                <select
                  value={documento.tipo}
                  onChange={(e) =>
                    setDocumento(
                      (prev) => ({
                        ...prev,
                        tipo: e.target.value,
                      })
                    )
                  }
                >
                  <option value="Factura">
                    Factura
                  </option>

                  <option value="Presupuesto">
                    Presupuesto
                  </option>
                </select>
              </label>

              <label>
                Fecha:
                <input
                  type="date"
                  value={documento.fecha}
                  onChange={(e) =>
                    setDocumento(
                      (prev) => ({
                        ...prev,
                        fecha: e.target.value,
                      })
                    )
                  }
                />
              </label>

              {/* EMISOR */}

              <label>
                Emisor:
                <select
                  value={
                    documento.emisor_id
                  }
                  onChange={(e) =>
                    seleccionarEmisor(
                      e.target.value
                    )
                  }
                >
                  {EMISORES.map(
                    (emisor) => (
                      <option
                        key={emisor.id}
                        value={emisor.id}
                      >
                        {emisor.nombre}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                NIF emisor:
                <input
                  type="text"
                  value={
                    documento.emisor_nif
                  }
                  onChange={(e) =>
                    setDocumento(
                      (prev) => ({
                        ...prev,
                        emisor_nif:
                          e.target.value,
                      })
                    )
                  }
                />
              </label>

              <label>
                Rol:
                <input
                  type="text"
                  value={
                    documento.emisor_rol
                  }
                  onChange={(e) =>
                    setDocumento(
                      (prev) => ({
                        ...prev,
                        emisor_rol:
                          e.target.value,
                      })
                    )
                  }
                />
              </label>

              {/* DESTINATARIO */}

              <label>
                Nombre destinatario:
                <input
                  type="text"
                  value={documento.nombre}
                  onChange={(e) =>
                    setDocumento(
                      (prev) => ({
                        ...prev,
                        nombre:
                          e.target.value,
                      })
                    )
                  }
                />
              </label>

              <label>
                NIF destinatario:
                <input
                  type="text"
                  value={documento.nif}
                  onChange={(e) =>
                    setDocumento(
                      (prev) => ({
                        ...prev,
                        nif: e.target.value,
                      })
                    )
                  }
                />
              </label>

              {/* IVA */}

              <label className="factura-iva-toggle">
                <input
                  type="checkbox"
                  checked={aplicaIva}
                  onChange={(e) =>
                    setAplicaIva(
                      e.target.checked
                    )
                  }
                />

                Aplicar IVA (21%)
              </label>

              {/* CUENTA */}

              <label>
                Número de cuenta:
                <input
                  type="text"
                  value={
                    documento.cuenta
                  }
                  onChange={(e) =>
                    setDocumento(
                      (prev) => ({
                        ...prev,
                        cuenta:
                          e.target.value,
                      })
                    )
                  }
                  placeholder="ES__ ____ ____ ____ ____ ____"
                />
              </label>

              {/* CONDICIONES */}

              <label>
                Condiciones de pago:
                <input
                  type="text"
                  value={
                    documento.condicionesPago
                  }
                  onChange={(e) =>
                    setDocumento(
                      (prev) => ({
                        ...prev,
                        condicionesPago:
                          e.target.value,
                      })
                    )
                  }
                  placeholder="Ej: El pago debe efectuarse entre los días 1 y 5 del mes"
                />
              </label>
            </div>

            {/* ========================================= */}
            {/* SERVICIOS */}
            {/* ========================================= */}

            <div
              style={{
                marginTop: "1.5rem",
              }}
            >
              <h4
                style={{
                  marginBottom: "0.8rem",
                }}
              >
                Servicios
              </h4>

              {servicios.map(
                (servicio, index) => (
                  <div
                    key={servicio.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(220px, 1fr) 150px auto",
                      gap: "10px",
                      alignItems: "end",
                      marginBottom: "10px",
                    }}
                  >
                    <label>
                      {index === 0
                        ? "Concepto / servicio:"
                        : `Concepto / servicio ${
                            index + 1
                          }:`}

                      <input
                        type="text"
                        value={
                          servicio.concepto
                        }
                        onChange={(e) =>
                          actualizarServicio(
                            servicio.id,
                            "concepto",
                            e.target.value
                          )
                        }
                        placeholder="Ej: Sesiones de psicología"
                      />
                    </label>

                    <label>
                      Importe (€):
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={
                          servicio.importe
                        }
                        onChange={(e) =>
                          actualizarServicio(
                            servicio.id,
                            "importe",
                            e.target.value
                          )
                        }
                        placeholder="0.00"
                      />
                    </label>

                    <button
                      type="button"
                      onClick={() =>
                        eliminarServicio(
                          servicio.id
                        )
                      }
                      title="Eliminar servicio"
                      style={{
                        height: "38px",
                        minWidth: "42px",
                        cursor: "pointer",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )
              )}

              <button
                type="button"
                className="btn"
                onClick={añadirServicio}
                style={{
                  marginTop: "5px",
                }}
              >
                + Añadir servicio
              </button>
            </div>

            {/* ========================================= */}
            {/* RESUMEN */}
            {/* ========================================= */}

            <div
              className="factura-resumen"
              style={{
                marginTop: "1.5rem",
              }}
            >
              <p>
                <strong>Base:</strong>{" "}
                {base.toFixed(2)} €
              </p>

              <p>
                <strong>IVA:</strong>{" "}
                {aplicaIva
                  ? `21% - ${iva.toFixed(
                      2
                    )} €`
                  : "Exento"}
              </p>

              <p>
                <strong>Total:</strong>{" "}
                {total.toFixed(2)} €
              </p>
            </div>

            {/* BOTÓN PDF */}

            <button
              className="btn-pdf"
              onClick={handleDownloadPDF}
              disabled={!documentoValido}
            >
              📄 Descargar{" "}
              {documento.tipo.toLowerCase()}
            </button>

            {/* ========================================= */}
            {/* DOCUMENTO QUE SE CONVIERTE EN PDF */}
            {/* ========================================= */}

            <div
              className="factura-container"
              ref={facturaRef}
              style={{
                marginTop: "1rem",
              }}
            >
              {/* CABECERA PDF */}

              <header className="factura-header">
                <img
                  src={logo}
                  alt="Te Ayudo"
                  className="factura-logo"
                />

                <div className="factura-info">
                  <p>{CENTRO.nombre}</p>
                  <p>{CENTRO.direccion}</p>
                  <p>{CENTRO.localidad}</p>
                  <p>
                    Tel: {CENTRO.telefono}
                  </p>
                </div>
              </header>

              <h2 className="factura-title">
                {documento.tipo}
              </h2>

              {/* DATOS GENERALES */}

              <div className="factura-details">
                <p>
                  <strong>Fecha:</strong>{" "}
                  {documento.fecha
                    ? new Date(
                        `${documento.fecha}T12:00:00`
                      ).toLocaleDateString(
                        "es-ES"
                      )
                    : "—"}
                </p>

                <p>
                  <strong>
                    Nº{" "}
                    {documento.tipo ===
                    "Presupuesto"
                      ? "presupuesto"
                      : "factura"}
                    :
                  </strong>{" "}
                  {numeroDocumento}
                </p>

                {/* EMISOR */}

                <p>
                  <strong>Emisor:</strong>{" "}
                  {documento.emisor_nombre}
                </p>

                {documento.emisor_nif && (
                  <p>
                    <strong>
                      NIF emisor:
                    </strong>{" "}
                    {documento.emisor_nif}
                  </p>
                )}

                {documento.emisor_rol && (
                  <p>
                    <strong>Rol:</strong>{" "}
                    {documento.emisor_rol}
                  </p>
                )}

                {/* DESTINATARIO */}

                {documento.nombre && (
                  <p>
                    <strong>
                      Destinatario:
                    </strong>{" "}
                    {documento.nombre}
                  </p>
                )}

                {documento.nif && (
                  <p>
                    <strong>NIF:</strong>{" "}
                    {documento.nif}
                  </p>
                )}
              </div>

              {/* TABLA SERVICIOS */}

              <table className="factura-table">
                <thead>
                  <tr>
                    <th>
                      Concepto / servicio
                    </th>

                    <th
                      style={{
                        textAlign: "right",
                      }}
                    >
                      Importe (€)
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {serviciosValidos.map(
                    (servicio) => (
                      <tr
                        key={`pdf-${servicio.id}`}
                      >
                        <td>
                          {
                            servicio.concepto
                          }
                        </td>

                        <td
                          style={{
                            textAlign:
                              "right",
                          }}
                        >
                          {Number(
                            servicio.importe
                          ).toFixed(2)}{" "}
                          €
                        </td>
                      </tr>
                    )
                  )}

                  {/* BASE */}

                  <tr>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: "bold",
                      }}
                    >
                      Base
                    </td>

                    <td
                      style={{
                        textAlign: "right",
                      }}
                    >
                      {base.toFixed(2)} €
                    </td>
                  </tr>

                  {/* IVA */}

                  <tr>
                    <td
                      style={{
                        textAlign: "right",
                        fontWeight: "bold",
                      }}
                    >
                      {aplicaIva
                        ? "IVA (21%)"
                        : "IVA (exento)"}
                    </td>

                    <td
                      style={{
                        textAlign: "right",
                      }}
                    >
                      {iva.toFixed(2)} €
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* TOTAL */}

              <div className="factura-total">
                Total: {total.toFixed(2)} €
              </div>

              {/* PIE */}

              <footer className="factura-footer">
                <p>
                  {documento.tipo} de
                  servicios profesionales
                </p>

                {documento.cuenta && (
                  <p>
                    Número de cuenta:{" "}
                    {documento.cuenta}
                  </p>
                )}

                {documento.condicionesPago && (
                  <p>
                    {
                      documento.condicionesPago
                    }
                  </p>
                )}
              </footer>
            </div>
          </section>
        </>
      )}
    </div>
  );
}